import {t, Trans} from '@lingui/macro';
import React from 'react';

import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {LocalizeRenderer} from '../../../../srcCommon/internationalization/internationalizationContext';
import {SchedulingLinkPropertiesEnum} from '../../../helpers/models/schedulingLinkHelpers';

export const schedulingLinkPropertyTooltips = {
  [SchedulingLinkPropertiesEnum.DOUBLE_BOOKING]: (
    <Trans id="schedulinglink-meetingsettings-doublebookingtooltip">
      Choose calendars you want to prevent double booking on. Time slots that overlap with existing events on
      these calendars will be hidden from visitors.
    </Trans>
  ),
  [SchedulingLinkPropertiesEnum.ADDITIONAL_GUESTS]: (
    <Trans id="scheduling-meetingsettings-additionalgueststooltip">
      Once the meeting is scheduled, also email an invite to these people.
    </Trans>
  ),
  [SchedulingLinkPropertiesEnum.MEETING_BUFFER]: (
    <Trans id="scheduling-meetingsettings-buffertimetooltip">
      Build in buffer time to prevent back-to-back meetings.
    </Trans>
  ),
  [SchedulingLinkPropertiesEnum.ADVANCE_NOTICE]: (
    <Trans id="scheduling-meetingsettings-advancednoticetooltip">
      Prevent people from booking meetings at the last minute.
    </Trans>
  ),
  [SchedulingLinkPropertiesEnum.START_TIME_INCREMENTS]: (
    <Trans id="scheduling-meetingsettings-startincrementstooltip">
      Control the start times of meeting slots that are presented to viewers of your scheduling link. For
      example, setting this to 30 minutes would produce meeting slots that start at 1:00, 1:30, and 2:00.
    </Trans>
  ),
  [SchedulingLinkPropertiesEnum.GREETING]: (
    <Trans id="scheduling-meetingsettings-greetingtooltip">
      Add a short greeting message for viewers of your scheduling link. The default greeting is “Pick a time
      to meet”.
    </Trans>
  ),
  [SchedulingLinkPropertiesEnum.DISPLAY_NAME]: (
    <Trans id="scheduling-meetingsettings-displaynametooltip">
      Instead of displaying your own name on this scheduling link, you can add a custom display name that’s
      shown to visitors.
    </Trans>
  ),
};
type SchedulingLinkPropertyWithPlaceholder =
  | typeof SchedulingLinkPropertiesEnum.TITLE
  | typeof SchedulingLinkPropertiesEnum.LOCATION
  | typeof SchedulingLinkPropertiesEnum.DESCRIPTION;
type SchedulingLinkPropertyPlaceholders = {
  [T in SchedulingLinkPropertyWithPlaceholder]: LocalizeRenderer;
};
export const schedulingLinkPropertyPlaceholders: SchedulingLinkPropertyPlaceholders = {
  [SchedulingLinkPropertiesEnum.TITLE]: (localize) =>
    localize(t('schedulinglink-meetingsettings-titleplaceholder')`Add a title`),
  [SchedulingLinkPropertiesEnum.LOCATION]: (localize) =>
    localize(t('schedulinglink-meetingsettings-locationplaceholder')`Add a location`),
  [SchedulingLinkPropertiesEnum.DESCRIPTION]: (localize) =>
    localize(t('schedulinglink-meetingsettings-descriptionplaceholder')`Add a description`),
};
export const noSchedulingLinksTitleCopy = (
  <Trans id="no-schedulinglinks-title">Schedule meetings faster with Scheduling Links!</Trans>
);
export const noSchedulingLinksSubtitleCopy = (
  <Trans id="no-schedulinglinks-subtitle">
    Choose your preferred meeting times and then share your link to allow others to book a meeting with you
    instantly.
  </Trans>
);
export const outdatedSchedulingLinkCopy = (
  <Trans id="schedulinglink-settings-timesoutdated">
    These available times are outdated. Try adding new availability.
  </Trans>
);
export const schedulingLearnMoreUrl =
  'https://help.frontapp.com/t/y7bzah/getting-started-with-scheduling-links';
export const schedulingLearnMoreInlineLinkButton = (
  <InlineLinkButton onClick={() => openInBrowser(schedulingLearnMoreUrl)}>
    <Trans id="scheduling-learn-more">Learn more</Trans>
  </InlineLinkButton>
);
